import * as React from "react";
import {
  Button,
  Box,
  Typography,
  Snackbar,
  Link,
  Paper,
  Popper,
  Fade,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";

import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { FormFieldTextField } from "../shared/components/FormFieldTextField";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { confirmResetPassword } from "aws-amplify/auth";
import { CheckBoxLabel } from "./components/CheckBoxLabel";
import { Visibility, VisibilityOff } from "@mui/icons-material";

interface ResetPasswordValues {
  password: string;
  confirmPassword: string;
}

export const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const confirmationTicket = searchParams.get("confirmationTicket");
  const email = searchParams.get("email");

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);

  const { control, handleSubmit, watch, formState } =
    useForm<ResetPasswordValues>({
      mode: "all",
      defaultValues: {
        password: "",
        confirmPassword: "",
      },
    });

  useEffect(() => {
    // Validate URL params on mount
    if (!confirmationTicket || !email) {
      setInvalidLink(true);
    }
  }, [confirmationTicket, email]);

  const handleResetPassword = async (data: ResetPasswordValues) => {
    if (!confirmationTicket || !email) {
      setMessage(t("auth.resetPassword.invalidLink"));
      return;
    }

    if (data.password !== data.confirmPassword) {
      setMessage(t("auth.resetPassword.mismatch"));
      return;
    }

    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: confirmationTicket,
        newPassword: data.password,
      });
      setSuccess(true);
    } catch (error: any) {
      if (
        error.name === "CodeMismatchException" ||
        error.message?.includes("Invalid verification code")
      ) {
        setMessage(t("auth.resetPassword.codeMismatch"));
        return;
      }
      if (
        error.name === "ExpiredCodeException" ||
        error.message?.includes("expired")
      ) {
        setMessage(t("auth.resetPassword.expired"));
        return;
      }
      if (error instanceof Error) {
        setMessage(error.message);
      } else {
        setMessage(t("auth.resetPassword.genericError"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (invalidLink) {
    return (
      <Paper elevation={1} sx={{ maxWidth: "80%", p: { xs: 3, sm: 4, lg: 5 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: "error.main", textAlign: "center" }}
          >
            {t("auth.resetPassword.invalidTitle")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", textAlign: "center" }}
          >
            {t("auth.resetPassword.invalidBody")}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/forgot-password")}
            sx={{ mt: 2 }}
            fullWidth
          >
            {t("auth.resetPassword.requestNew")}
          </Button>
          <Link
            sx={{ textDecoration: "none", color: "primary.main" }}
            variant="body2"
            component={RouterLink}
            to="/login"
          >
            {t("common.backToLogin")}
          </Link>
        </Box>
      </Paper>
    );
  }

  if (success) {
    return (
      <Paper elevation={1} sx={{ maxWidth: "80%", p: { xs: 3, sm: 4, lg: 5 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: "success.main", textAlign: "center" }}
          >
            {t("auth.resetPassword.successTitle")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", textAlign: "center" }}
          >
            {t("auth.resetPassword.successBody")}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{ mt: 2 }}
            fullWidth
          >
            {t("auth.resetPassword.signInButton")}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ maxWidth: "80%", p: { xs: 3, sm: 4, lg: 5 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          {t("auth.resetPassword.title")}
        </Typography>
        <Typography component="div" sx={{ color: "text.secondary", mt: 1 }}>
          {t("auth.resetPassword.subtitlePrefix")}{" "}
          <Typography
            component="span"
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            {email}
          </Typography>
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit(handleResetPassword)}
          noValidate
          sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Popper
            sx={{ zIndex: 9999, fontSize: ".1rem" }}
            open={open}
            anchorEl={anchorEl}
            placement={"top-end"}
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={150}>
                <Paper sx={{ border: 1, padding: 2, borderRadius: "1rem" }}>
                  <Typography sx={{ pb: 1, fontSize: ".75rem" }}>
                    {t("auth.resetPassword.requirementsTitle")}
                  </Typography>
                  <Box>
                    <CheckBoxLabel
                      label={t("auth.resetPassword.reqUppercase")}
                      checked={Boolean(watch("password")?.match(/[A-Z]/))}
                    />
                    <CheckBoxLabel
                      label={t("auth.resetPassword.reqSpecial")}
                      checked={Boolean(
                        watch("password")?.match(/[#?!@$%^&*-]/)
                      )}
                    />
                    <CheckBoxLabel
                      label={t("auth.resetPassword.reqNumber")}
                      checked={Boolean(watch("password")?.match(/[0-9]/))}
                    />
                    <CheckBoxLabel
                      label={t("auth.resetPassword.reqLength")}
                      checked={Boolean(
                        watch("password") && watch("password")!.length >= 10
                      )}
                    />
                  </Box>
                </Paper>
              </Fade>
            )}
          </Popper>

          <FormFieldTextField
            control={control}
            required
            fullWidth
            name="password"
            label={t("auth.resetPassword.newPasswordLabel")}
            type={showPassword ? "text" : "password"}
            id="password"
            rules={{
              required: t("auth.resetPassword.passwordRequired"),
              minLength: {
                value: 10,
                message: t("auth.resetPassword.passwordMinLength"),
              },
              pattern: {
                value:
                  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{10,}$/,
                message: t("auth.resetPassword.passwordFormat"),
              },
            }}
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
              setOpen(true);
            }}
            onBlur={() => {
              setOpen(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormFieldTextField
            control={control}
            required
            fullWidth
            name="confirmPassword"
            label={t("auth.resetPassword.confirmPasswordLabel")}
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            autoComplete="new-password"
            rules={{
              required: t("auth.resetPassword.confirmRequired"),
              validate: (val: string) => {
                if (val !== watch("password")) {
                  return t("auth.resetPassword.passwordsMustMatch");
                }
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!formState.isValid || loading}
            sx={{
              mt: 2,
              mb: 2,
              fontSize: "1rem",
              backgroundColor: "primary.main",
              fontWeight: 600,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("auth.resetPassword.submit")
            )}
          </Button>

          <Box sx={{ textAlign: "center" }}>
            <Link
              sx={{ textDecoration: "none", color: "primary.main" }}
              variant="body2"
              component={RouterLink}
              to="/login"
            >
              {t("common.backToLogin")}
            </Link>
          </Box>
        </Box>

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={message !== null}
          autoHideDuration={6000}
          message={message}
          onClose={() => setMessage(null)}
          ContentProps={{
            sx: {
              borderRadius: "5px",
              color: "white",
              bgcolor: "error.main",
              fontWeight: "bold",
              fontSize: "1rem",
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default ResetPassword;
